import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { FormikErrors, useFormik } from 'formik';

import {
  convertRequestErrorToMap,
  useMedia,
  validators,
} from '@tager/web-core';

import SuccessForm from '@/components/Forms/SuccessForm';
import { colors } from '@/constants/theme';
import { media } from '@/utils/mixin';
import TextInput from '@/components/TextInput';
import { CheckboxAgreement } from '@/components/Checkbox';
import { ButtonPrimary } from '@/components/CarCard/components/Button';
import Select from '@/components/Select';
import { useServiceContext } from '@/modules/Services/Services.hooks';
import Loader from '@/components/Loader';
import { OptionType } from '@/typings/common';
import { googleEvent } from '@/utils/events';
import { submitServiceKo } from '@/services/leads/leads-service';
import { captchaFormStyled } from '@/components/YandexInvisibleCaptcha/CaptchaForm';
import { useFormikCaptchaForm } from '@/components/YandexInvisibleCaptcha/useFormikCaptchaForm';

interface Props {
  title: string;
  description: string;
  options: OptionType[];
  event?: string;
}

interface IConsultation {
  name: string;
  phone: string;
  service: string;
}

const requiredMessage = 'required';

function ServiceConsultationForm({
  title,
  description,
  options,
  event,
}: Props) {
  const isMobile = useMedia('(max-width: 767px)');
  const [isRequestAccept, setIsRequestAccept] = useState(false);
  const [userAgreement, setUserAgreement] = useState(false);
  const [loading, setLoading] = useState(false);
  const { selectedValue, setSelectedValue } = useServiceContext();

  function handleValidateConsultationForm(
    values: IConsultation
  ): FormikErrors<IConsultation> {
    const errors: FormikErrors<IConsultation> = {};
    if (validators.required(values.name)) {
      errors.name = requiredMessage;
    }

    if (!values.phone) {
      errors.phone = requiredMessage;
    }

    return errors;
  }

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setErrors,
    setSubmitting,
    setTouched,
    validateForm,
  } = useFormik<IConsultation>({
    initialValues: { name: '', phone: '', service: '' },
    validate: handleValidateConsultationForm,
    onSubmit: async () => {},
  });

  const captchaFormProps = useFormikCaptchaForm({
    values,
    validateForm,
    setTouched,
    setSubmitting,
    canSubmit: () =>
      Boolean(
        userAgreement && values.name && !errors.phone && selectedValue.value
      ),
    touchAllOnError: { name: true, phone: true },
    onSubmit: async (payload) => {
      setIsRequestAccept(false);
      setLoading(true);

      const newValues = {
        ...payload,
        service: selectedValue.label,
      };

      try {
        await submitServiceKo(newValues).then(() => {
          if (event) {
            googleEvent(event);
          }
        });
        setIsRequestAccept(true);
      } catch (error: any) {
        console.log(error);
        const errorMap = convertRequestErrorToMap(
          error
        ) as FormikErrors<IConsultation>;
        setErrors(errorMap);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Component isRequestAccept={isRequestAccept}>
      {isRequestAccept ? (
        <SuccessForm />
      ) : (
        <Content isMobile={isMobile}>
          <Title>{title}</Title>
          <Description>{description}</Description>
          <StyledForm {...captchaFormProps} isMobile={isMobile}>
            <FormContainer isMobile={isMobile}>
              <FormContainerName isMobile={isMobile}>
                <TextInput
                  id="name"
                  name="name"
                  label="Имя"
                  placeholder="Имя"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  requiredField
                  errorMessage={touched.name && errors.name ? ' ' : undefined}
                />
              </FormContainerName>
              <FormContainerPhone isMobile={isMobile}>
                <TextInput
                  id="phone"
                  name="phone"
                  label="Телефон"
                  placeholder="+375 (__) ___-__-__"
                  value={values.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  requiredField
                  errorMessage={touched.phone && errors.phone ? ' ' : undefined}
                />
              </FormContainerPhone>
              <FormContainerService isMobile={isMobile}>
                <Select
                  label="Выберите услугу"
                  value={selectedValue}
                  options={options}
                  onChange={setSelectedValue}
                  isRequired
                />
              </FormContainerService>
            </FormContainer>
            <CheckboxAgreementWrapper isMobile={isMobile}>
              <CheckboxAgreement
                checked={userAgreement}
                onChange={() => setUserAgreement(!userAgreement)}
              />
            </CheckboxAgreementWrapper>
            <ButtonWrapper>
              <Button
                type="submit"
                isMobile={isMobile}
                disabled={
                  !values.name ||
                  Boolean(errors.phone) ||
                  !userAgreement ||
                  !selectedValue.value
                }
              >
                {loading ? <Loader color={'#fff'} /> : 'заказать звонок'}
              </Button>
            </ButtonWrapper>
          </StyledForm>
        </Content>
      )}
    </Component>
  );
}

export default ServiceConsultationForm;

const Component = styled.div<{ isRequestAccept: boolean }>`
  position: relative;
  border-radius: 17px;

  display: ${(props): string => (props.isRequestAccept ? 'flex' : '')};
  align-items: ${(props): string => (props.isRequestAccept ? 'center' : '')};
  justify-content: ${(props): string =>
    props.isRequestAccept ? 'center' : ''};

  background: ${(props) => props.theme.white_1};
`;

const Content = styled.div<{ isMobile: boolean }>`
  padding: ${(props): string => (props.isMobile ? '30px 20px' : '0 20px')};
  margin: 0 auto;
  box-shadow: ${(props) => props.theme.blackShadow100};
  height: fit-content;
  border-radius: 17px;
`;

const Title = styled.h2`
  font-size: 36px;
  padding: 48px 0 0;
  text-align: center;
  color: ${(props) => props.theme.black};

  ${media.mobile(css`
    font-size: 30px;
    padding: 0;
  `)}
`;

const Description = styled.span`
  display: block;
  color: ${colors.gray};
  margin-top: 24px;
  font-size: 16px;
  text-align: center;
`;

const StyledForm = captchaFormStyled<IConsultation>()<{ isMobile: boolean }>`
  padding-bottom: ${(props): string => (props.isMobile ? '0' : '55px')};
`;

const FormContainer = styled.div<{ isMobile: boolean }>`
  padding: ${(props): string => (props.isMobile ? '0' : '0 28px')};
  margin: 40px auto 0;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  ${media.mobile(
    css`
      justify-content: normal;
      flex-direction: column;
      align-items: center;
    `
  )}
`;

const FormContainerName = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  width: ${(props): string => (props.isMobile ? '100%' : '384px')};
  margin-right: ${(props): string => (props.isMobile ? '0' : '24px')};
`;

const FormContainerPhone = styled(FormContainerName)`
  @media (max-width: 1170px) {
    margin-top: 30px;
  }
`;

const FormContainerService = styled(FormContainerPhone)``;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const Button = styled(ButtonPrimary)<{
  isMobile: boolean;
}>`
  position: relative;
  height: 50px;
  padding: 11px 0;
  width: ${(props): string => (props.isMobile ? '100%' : '384px')};
  margin-top: 35px;
  font-size: 16px;
  border-radius: 8px;
`;

const CheckboxAgreementWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  margin: 14px 30px 0;

  ${({ isMobile }) =>
    isMobile &&
    css`
      margin: 14px 0 0;
    `}
`;
